import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToastNotification } from '../../contexts/ToastContext';

/**
 * SessionWarning Component (SessionExpiringNotification)
 *
 * A notification banner that warns users when their session is about to expire.
 * Shows a live countdown timer and provides "Extend Session" and "Log Out" actions.
 *
 * Features:
 * - Live MM:SS countdown timer
 * - Smooth slide-up/fade-out animations
 * - Glassmorphism design with amber accent
 * - Persists across page refresh via localStorage
 * - Auto-logout when timer hits 0
 */
const SessionWarning = () => {
  const toast = useToastNotification();
  const { 
    sessionWarning, 
    sessionInfo, 
    extendSession,
    logout,
    checkSession
  } = useAuth();

  const [showWarning, setShowWarning] = useState(false);
  const [displayTime, setDisplayTime] = useState(null);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);
  const [extending, setExtending] = useState(false);

  // Store the expiration timestamp (not just seconds remaining)
  const expirationTimeRef = useRef(null);

  // Warning threshold in seconds (5 minutes = 300 seconds)
  const warningThreshold = 300;

  /**
   * Update warning state based on session info
   */
  const updateWarningState = useCallback(() => {
    // First check localStorage for persisted state (survives page refresh)
    const storedTimestamp = localStorage.getItem('sessionExpirationTimestamp');
    if (storedTimestamp && !sessionInfo) {
      const expirationTimestamp = parseInt(storedTimestamp, 10);
      const remainingMs = expirationTimestamp - Date.now();
      const remainingSeconds = Math.floor(remainingMs / 1000);

      if (remainingSeconds > 0 && remainingSeconds < warningThreshold) {
        expirationTimeRef.current = expirationTimestamp;
        setShowWarning(true);
        setIsAnimatingIn(true);
        setDisplayTime(remainingSeconds);
        setTimeout(() => setIsAnimatingIn(false), 50);
        return;
      } else {
        localStorage.removeItem('sessionExpirationTimestamp');
      }
    }

    // If sessionWarning from AuthContext is true, show the warning
    if (sessionWarning && sessionInfo) {
      const timeRemaining = sessionInfo.effective_remaining;

      // Calculate and store expiration timestamp
      const expirationTimestamp = Date.now() + (timeRemaining * 1000);
      expirationTimeRef.current = expirationTimestamp;

      // Persist to localStorage so it survives page refreshes
      localStorage.setItem('sessionExpirationTimestamp', expirationTimestamp.toString());

      if (!showWarning) {
        setShowWarning(true);
        setIsAnimatingOut(false);
        setIsAnimatingIn(true);
        // Trigger slide-up animation after brief delay
        setTimeout(() => {
          setIsAnimatingIn(false);
        }, 50);
      }
      
      setDisplayTime(timeRemaining);
    } else if (!sessionWarning) {
      // Session is healthy, hide warning
      setShowWarning(false);
      expirationTimeRef.current = null;
      localStorage.removeItem('sessionExpirationTimestamp');
    }
  }, [sessionWarning, sessionInfo, showWarning, warningThreshold]);

  // Update warning state when session info changes
  useEffect(() => {
    updateWarningState();
  }, [updateWarningState]);

  // Live countdown effect
  useEffect(() => {
    if (!showWarning || !expirationTimeRef.current) return;

    const countdownInterval = setInterval(() => {
      const remainingMs = expirationTimeRef.current - Date.now();
      const remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000));

      setDisplayTime(remainingSeconds);

      if (remainingSeconds <= 0) {
        clearInterval(countdownInterval);
        localStorage.removeItem('sessionExpirationTimestamp');
        // Session expired - logout
        logout();
      }
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [showWarning, logout]);

  /**
   * Handle extend session - resets BOTH idle and absolute timeout
   */
  const handleExtendSession = async () => {
    setExtending(true);
    
    // Start fade-out animation
    setIsAnimatingOut(true);

    // Wait for animation to complete (300ms)
    setTimeout(() => {
      setShowWarning(false);
      setIsAnimatingOut(false);
      expirationTimeRef.current = null;
      setDisplayTime(null);
      localStorage.removeItem('sessionExpirationTimestamp');
    }, 300);

    try {
      const success = await extendSession();

      if (!success) {
        console.warn('Session extension may have failed');
      }

      // Verify extension after a brief delay
      setTimeout(async () => {
        try {
          await checkSession();
        } catch (err) {
          console.error('Failed to verify session after extend:', err);
        }
        setExtending(false);
      }, 1000);
    } catch (error) {
      console.error('Failed to extend session:', error);
      setExtending(false);
      toast.error('Failed to extend session. Please try logging in again.');
    }
  };

  /**
   * Handle logout
   */
  const handleLogout = async () => {
    localStorage.removeItem('sessionExpirationTimestamp');
    try {
      await logout();
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  /**
   * Format time as MM:SS
   */
  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Don't render if no warning and not animating
  if (!showWarning && !isAnimatingOut) return null;

  return (
    <div
      data-testid="session-warning"
      className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl px-4 ${
        isAnimatingOut || isAnimatingIn
          ? 'opacity-0 translate-y-4'
          : 'opacity-100 translate-y-0'
      }`}
      style={{
        transition: 'opacity 300ms ease-in-out, transform 300ms ease-in-out'
      }}
    >
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 shadow-lg backdrop-blur-sm">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-amber-400 mb-1">
              Session Expiring Soon
            </h3>
            <p className="text-xs text-zinc-300">
              Your session will expire in{' '}
              <span className="font-mono font-bold text-amber-400">
                {formatTime(displayTime)}
              </span>
              . Click below to extend your session.
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={handleExtendSession}
              disabled={extending}
              className="text-xs font-medium px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-600 text-black transition-colors whitespace-nowrap disabled:opacity-50"
              data-testid="session-extend-btn"
            >
              {extending ? 'Extending...' : 'Extend Session'}
            </button>
            <button
              onClick={handleLogout}
              className="text-xs font-medium px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white transition-colors whitespace-nowrap"
              data-testid="session-logout-btn"
            >
              Log Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionWarning;
